import { useEffect, useState } from "react";
import { IonAlert } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import pushNotificationService from "../../services/pushNotifications";
import { snapshotOverlays } from "../../services/overlayDiag";
import { diagEvent } from "../../services/diag";
import { getAuthToken } from "../../services/authStorage";
import {
  hasHappenedThisLaunch,
  markHappenedThisLaunch,
} from "../../utils/oncePerLaunch";

const SHOWN_THIS_LAUNCH = "permissionGate";

/**
 * Cold-start permission gate.
 *
 * Once per app launch, if NOTIFICATIONS are DENIED, show an explanatory alert
 * with a button that deep-links to the OS app settings. The native permission
 * dialog cannot be re-shown after the user has denied it once (standard
 * iOS/Android behaviour), so the only way to recover the opt-in is to send the
 * user to Settings — which is what this does.
 *
 * Location is deliberately NOT handled here. It used to be, which meant users
 * saw two different location popups — the OS dialog from the home dashboard and
 * this app-styled "Più tardi / Apri Impostazioni" alert — and because dismissing
 * this one persists nothing, it reappeared on every cold start. Location now has
 * exactly one prompt (the system dialog), asked once at startup and thereafter
 * only from the Mappa tab, where the denied state has its own inline recovery
 * copy pointing at device settings.
 *
 * Native only (the web has no OS-level permission prompt). Dismissible with
 * "Più tardi". Mounted at the app root — but its mount effect also runs after
 * every in-app full reload (leaving the Shop, «Vai al profilo», the back-to-Home
 * rule), which re-showed it within minutes. So it shows at most once per app
 * launch: the next real launch asks again, a background→foreground resume never
 * does.
 */
export default function PermissionGate() {
  const [open, setOpen] = useState(false);
  const [missing, setMissing] = useState([]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Only nag signed-in users, so it never overlaps login/onboarding.
    if (!getAuthToken()) return;
    if (hasHappenedThisLaunch(SHOWN_THIS_LAUNCH)) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const push = await pushNotificationService
          .checkPermissionState()
          .catch(() => "unknown");
        const miss = [];
        if (push === "denied") miss.push("le notifiche");
        if (!cancelled && miss.length) {
          markHappenedThisLaunch(SHOWN_THIS_LAUNCH);
          setMissing(miss);
          setOpen(true);
        }
      } catch {
        /* best-effort — never block the app on a permission check */
      }
    }, 3000); // let the splash / initial route settle first

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  // DIAG: record every open/close of this gate with the app's visibility state,
  // to catch it presenting/dismissing WHILE backgrounded (paused rAF -> stuck
  // animation -> orphaned backdrop, the "dimmed Home, no popup" report).
  useEffect(() => {
    diagEvent("diag_gate", {
      gate: "permission",
      open: open ? 1 : 0,
      hidden: typeof document !== "undefined" && document.hidden ? 1 : 0,
    });
  }, [open]);

  const openSettings = async () => {
    setOpen(false);
    try {
      const { NativeSettings, AndroidSettings, IOSSettings } = await import(
        "capacitor-native-settings"
      );
      if (Capacitor.getPlatform() === "android") {
        // App details page exposes both Permissions (location) and Notifications.
        await NativeSettings.open({
          optionAndroid: AndroidSettings.ApplicationDetails,
        });
      } else {
        await NativeSettings.open({ optionIOS: IOSSettings.App });
      }
    } catch {
      /* ignore — user can still open settings manually */
    }
  };

  // Notifications are the only permission left here, so the copy can name it
  // directly — the old template produced "ha bisogno di le notifiche".
  const label = missing.join(" e ") || "le notifiche";

  return (
    <IonAlert
      isOpen={open}
      onDidDismiss={() => {
        setOpen(false);
        // DIAG: after this alert tears down, check whether a backdrop (its own,
        // or another overlay's — e.g. a stranded ForceUpdate modal underneath)
        // is left on top of Home swallowing taps. Delay so the dismiss
        // animation has finished before we sample.
        setTimeout(() => snapshotOverlays("perm_dismiss"), 400);
      }}
      header="Attiva i permessi"
      message={`Per funzionare al meglio, Dieta Social ha bisogno del tuo consenso per ${label}. Puoi attivarle dalle Impostazioni del telefono.`}
      buttons={[
        { text: "Più tardi", role: "cancel" },
        { text: "Apri Impostazioni", handler: openSettings },
      ]}
    />
  );
}
